import { useState } from 'react';
import { motion } from 'framer-motion';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  Shield,
  AlertTriangle,
  Lock,
  Globe,
  Bot,
  TrendingUp,
  Clock,
  MapPin,
  Hash,
  Ban,
  Activity,
  Download,
  RefreshCw,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/integrations/supabase/client';
import { SecurityEventsList } from '@/components/security/SecurityEventsList';
import { SecurityMetricCard } from '@/components/security/SecurityMetricCard';
import { ThreatAnalytics } from '@/components/security/ThreatAnalytics';
import { BlockedIPsList } from '@/components/security/BlockedIPsList';

export default function Security() {
  const { user, isLoading: authLoading } = useAuthStore();
  const [timeRange, setTimeRange] = useState('7');

  const { data: stats, isLoading: statsLoading, refetch } = useQuery({
    queryKey: ['security-stats', user?.id, timeRange],
    queryFn: async () => {
      if (!user?.id) return null;

      const startDate = subDays(new Date(), parseInt(timeRange));

      // Get all security events for the user's QR codes
      const { data: qrCodes } = await supabase
        .from('qr_codes')
        .select('id')
        .eq('user_id', user.id);

      if (!qrCodes?.length) {
        return {
          totalEvents: 0,
          blockedAttempts: 0,
          passwordFailures: 0,
          geoBlocks: 0,
          botDetections: 0,
          eventsByType: {},
          eventsByDay: [],
          topCountries: [],
          recentEvents: [],
        };
      }

      const qrCodeIds = qrCodes.map((q) => q.id);

      const { data: events } = await supabase
        .from('security_audit_logs')
        .select('*')
        .in('qr_code_id', qrCodeIds)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      const allEvents = events || [];

      // Calculate stats
      const totalEvents = allEvents.length;
      const blockedAttempts = allEvents.filter((e) => e.is_blocked).length;
      const passwordFailures = allEvents.filter((e) =>
        ['password_failed', 'password_attempt_locked'].includes(e.event_type)
      ).length;
      const geoBlocks = allEvents.filter((e) => e.event_type === 'geo_blocked').length;
      const botDetections = allEvents.filter((e) => e.event_type === 'bot_detected').length;

      // Events by type
      const eventsByType: Record<string, number> = {};
      allEvents.forEach((e) => {
        eventsByType[e.event_type] = (eventsByType[e.event_type] || 0) + 1;
      });

      // Events by day
      const eventsByDay: { date: string; count: number; blocked: number }[] = [];
      const dayMap: Record<string, { count: number; blocked: number }> = {};
      allEvents.forEach((e) => {
        const day = format(new Date(e.created_at), 'yyyy-MM-dd');
        if (!dayMap[day]) dayMap[day] = { count: 0, blocked: 0 };
        dayMap[day].count++;
        if (e.is_blocked) dayMap[day].blocked++;
      });
      Object.entries(dayMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([date, data]) => {
          eventsByDay.push({ date, ...data });
        });

      // Top countries
      const countryMap: Record<string, number> = {};
      allEvents.forEach((e) => {
        if (e.country) {
          countryMap[e.country] = (countryMap[e.country] || 0) + 1;
        }
      });
      const topCountries = Object.entries(countryMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([country, count]) => ({ country, count }));

      return {
        totalEvents,
        blockedAttempts,
        passwordFailures,
        geoBlocks,
        botDetections,
        eventsByType,
        eventsByDay,
        topCountries,
        recentEvents: allEvents.slice(0, 50),
      };
    },
    enabled: !!user?.id,
  });

  if (authLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const handleExport = () => {
    if (!stats?.recentEvents?.length) return;

    const csv = [
      ['Date', 'Event', 'QR Code', 'IP', 'Country', 'City', 'Blocked', 'Details'].join(','),
      ...stats.recentEvents.map((e) =>
        [
          format(new Date(e.created_at), 'yyyy-MM-dd HH:mm:ss'),
          e.event_type,
          e.qr_code_id,
          e.ip_address || '-',
          e.country || '-',
          e.city || '-',
          e.is_blocked ? 'Yes' : 'No',
          JSON.stringify(e.event_data || {}),
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Shield className="h-8 w-8 text-primary" />
                Security Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Monitor security events and threats across all your QR codes
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={handleExport} disabled={!stats?.recentEvents?.length}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8"
        >
          <SecurityMetricCard
            title="Total Events"
            value={stats?.totalEvents || 0}
            icon={Activity}
            loading={statsLoading}
          />
          <SecurityMetricCard
            title="Blocked Attempts"
            value={stats?.blockedAttempts || 0}
            icon={Ban}
            loading={statsLoading}
            variant="destructive"
          />
          <SecurityMetricCard
            title="Password Failures"
            value={stats?.passwordFailures || 0}
            icon={Lock}
            loading={statsLoading}
            variant="warning"
          />
          <SecurityMetricCard
            title="Geo Blocks"
            value={stats?.geoBlocks || 0}
            icon={Globe}
            loading={statsLoading}
          />
          <SecurityMetricCard
            title="Bot Detections"
            value={stats?.botDetections || 0}
            icon={Bot}
            loading={statsLoading}
            variant="warning"
          />
        </motion.div>

        {/* Main Content */}
        <Tabs defaultValue="events" className="space-y-6">
          <TabsList>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Events
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="blocked" className="flex items-center gap-2">
              <Ban className="h-4 w-4" />
              Blocked IPs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <SecurityEventsList events={stats?.recentEvents || []} loading={statsLoading} />
          </TabsContent>

          <TabsContent value="analytics">
            <ThreatAnalytics
              eventsByType={stats?.eventsByType || {}}
              eventsByDay={stats?.eventsByDay || []}
              topCountries={stats?.topCountries || []}
              loading={statsLoading}
            />
          </TabsContent>

          <TabsContent value="blocked">
            <BlockedIPsList />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
